<template>
  <div style="margin-top: -4px">
    <div v-if="!isLoading && !pod">
      <v-row >
        <v-col>
          <h1>Cannot find the file...</h1>
        </v-col>
      </v-row>
    </div>

    <div v-else-if="isLoading && !pod">
      <v-row class="pod-loading-spinner">
        <v-col class="text-center">
          <v-progress-circular
            indeterminate
            size="70"
            width="7"
            color="primary"
            :aria-label="$t('routes.pod.loadingFile')"
          />
          <div>{{ $t('routes.pod.loadingFile') }}</div>
        </v-col>
      </v-row>
    </div>

    <div v-if="pod">
      <PodToolsBar />

      <v-row v-if="((isVideoPod || isAudioPod) && $canRunFreeService() && canTranscodedFile)"
             justify="center" class="mt-2">
        <v-col class="d-flex justify-center">
          <v-btn
            v-if="canTranscodedFile"
            v-on:click.prevent="transcodeFile"
            v-bind:disabled="preventTranscodingDoubleclick"
            color="primary"
            elevation=0
          >
            <v-icon start>mdi-video-box</v-icon>
            {{ $t('routes.pod.menuTools.runTranscode') }}
          </v-btn>

          <div v-if="errorMessage" class="bg-error" data-cy="">
            {{ errorMessage }}
          </div>

          <div v-if="errorTranslatable" class="bg-error">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>
        </v-col>
      </v-row>

      <div v-if="pod && pod.launchedServices && pod.launchedServices.length > 0"
           class="launched-services-container" style="">
        <div class="launched-services-dot" style="">
          <v-tooltip location="bottom" :aria-label="launchedServicesTooltipLabel">
            <template v-slot:activator="{ props }">
              <div style="width: 100%; height: 100%;" v-bind="props">
              </div>
            </template>

            <div style="display: inline">
              <ul class="px-2">
                <li v-for="item in pod.launchedServices" :key="item.id">
                  {{ $replaceT(item.code, item.params, item.content) }}
                </li>
              </ul>
            </div>
          </v-tooltip>
        </div>
      </div>

      <v-row v-if="isVideoPod" justify="center" >
        <v-col>
          <PlyrPlayer
            v-if="hasTranscodedFile"
            v-bind:pod="pod"
            :comments="getCommentsWithStart"
            allow-source-selection
            allow-quality-selection
          />

          <div v-else>
            <NoPreviewAvailable assetType="video" />
          </div>
        </v-col>
      </v-row>

      <v-row v-if="isAudioPod" justify="center" >
        <v-col>
          <AudioPlayer v-if="hasTranscodedFile" v-bind:pod="pod" :comments="getCommentsWithStart" />
          <NoPreviewAvailable v-else assetType="audio" />
        </v-col>
      </v-row>

      <v-row v-if="isImagePod" justify="center" >
        <v-col>
          <PodImageView v-if="hasThumbnailFile" v-bind:pod="pod" />
          <NoPreviewAvailable v-else assetType="image" />
        </v-col>
      </v-row>

      <v-row v-if="isPdfPod || isDocxPod">
        <v-col v-if="isPdfPod" cols="12" class="d-flex justify-center" ref="embedPdfContainer">
          <EmbedPdfPreview :asset="getRawAsset" />
        </v-col>
        <v-col v-else cols="12" class="d-flex justify-center">
          <DocxFilePreview :url="getRawAsset.url"/>
        </v-col>
      </v-row>

      <v-row v-if="isArchivePod">
        <v-col class="text-center" v-if="isRefreshingPodArchiveContents">
          <v-progress-circular
            indeterminate
            size="70"
            width="7"
            color="primary"
            :aria-label="$t('routes.pod.loadingArchiveContents')"
          />
          <div>{{ $t('routes.pod.loadingArchiveContents') }}</div>
        </v-col>
        <v-col v-else-if="listArchiveContentsError">
          <div class="bg-error pa-3 rounded d-flex align-center ga-2">
            <v-icon icon="mdi-lock" />
            {{ $replaceT(listArchiveContentsError.message.code, listArchiveContentsError.message.params, listArchiveContentsError.message.content) }}
          </div>
        </v-col>
        <v-col v-else-if="archiveContents !== null" cols="9">
          <v-treeview
            v-model:opened="openedTreeNode"
            :items="archiveContents"
            density="compact"
            item-title="name"
            item-value="id"
            activatable
            open-on-click
          >
            <template v-slot:prepend="{ item, isOpen }">
              <v-icon v-if="item.type === 'd'" :icon="isOpen ? 'mdi-folder-open' : 'mdi-folder'"/>
              <v-icon v-else icon="mdi-file"/>
            </template>

            <template v-slot:append="{ item }">
              <div v-if="item.type === 'f'">{{ formatSize(item.size) }}</div>
            </template>
          </v-treeview>
        </v-col>
      </v-row>

      <PodArchivedDialog
        v-if="pod"
        v-model:shouldShowArchivedPodDialog="shouldShowArchivedPodDialog"
      />
    </div>

    <PodDeletedDialog
      v-bind:pod="currentPod"
      v-model:shouldShowDeletedPodDialog="shouldShowDeletedPodDialog"
    />
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { usePodLabelStore } from '@/stores/PodLabelStore.js';
import { usePodCommentStore } from '@/stores/PodCommentStore.js';
import MimetypeHelper from '@/components/MimetypeHelper.js';
import PodToolsBar from "./components/podToolsBar/PodToolsBar.vue"
import PodImageView from './components/PodImageView.vue'
import AudioPlayer from  './components/players/AudioPlayer.vue'
import PlyrPlayer from './components/players/PlyrPlayer.vue'
import NoPreviewAvailable from './components/NoPreviewAvailable.vue'
import PodDeletedDialog from './components/dialogs/PodDeletedDialog.vue'
import PodArchivedDialog from './components/dialogs/PodArchivedDialog.vue'
import DocxFilePreview from '@/routes/pods2/components/DocxFilePreview.vue';
import EmbedPdfPreview from '@/routes/pods2/components/EmbedPdfPreview.vue';
import { formatSize } from '@/components/formatters.js';

export default {
  name: "PodIndex",
  components: {
    EmbedPdfPreview,
    DocxFilePreview,
    PodToolsBar,
    PlyrPlayer,
    AudioPlayer,
    PodImageView,
    NoPreviewAvailable,
    PodDeletedDialog,
    PodArchivedDialog,
  },
  data() {
    return {
      podId: '',
      parentNodeId: '',

      preventTranscodingDoubleclick: false,
      isLoading: true,

      errorMessage: "",
      errorTranslatable: null,

      shouldShowDeletedPodDialog: false,
      shouldShowArchivedPodDialog: false,
      currentPod: {
        id: null,
        nodeid: null,
      },
      openedTreeNode: null,
    };
  },
  beforeMount() {
    this.loadPodAbortController = new AbortController();
  },
  mounted() {
    const podStore = usePodStore();
    podStore._resetViewPodEvents();
  },
  beforeUnmount() {
    const podStore = usePodStore();
    podStore.resetPods();

    this.loadPodAbortController.abort();
  },
  watch: {
    '$route.params.id': {
      immediate: true, // Trigger the watcher immediately when the component is created
      handler() {
        this.updateCurrentPod();
      }
    },
    isViewPodDeleted: function(newValue) {
      if (!newValue) return;

      if (!this.currentPod || !this.currentPod.id || !this.currentPod.nodeid) return;

      const podStore = usePodStore();

      const latestDeletedPodIds = podStore.getLatestDeletedPodIds;

      // Check if the current pod ID is in the latest deleted pods by me.
      if (latestDeletedPodIds.includes(this.currentPod.id)) {
        podStore.removeLatestDeletedPodId(this.currentPod.id);

        if (this.currentPod.nodeid) {
          this.$router.push(`/node/${this.currentPod.nodeid}`);
        } else {
          this.$router.push('/');
        }
      } else {
        this.shouldShowDeletedPodDialog = newValue;
      }
    },
    isViewPodArchived: function(newValue) {
      this.shouldShowArchivedPodDialog = newValue;
    },
    pod:function(newValue) {
      if (newValue?.id) {
        this.currentPod = {
          id: newValue.id,
          nodeid: newValue.nodeid,
        }
      }
    }
  },
  methods: {
    formatSize,
    updateCurrentPod() {
      this.podId = this.$route.params.id || '';
      this.isLoading = true;

      let self = this;
      const podStore = usePodStore();
      podStore.viewPod(this.podId, this.loadPodAbortController)
      .then(function(pod) {
        self.isLoading = false;

        if (!pod) {
          return;
        }

        self.selectedPodStatus = pod.badgestatus;

        const nodeStore = useNodeStore();
        nodeStore.setActiveNode(pod.nodeid);

        if (self.$canListPodService()) {
          const podCommentStore = usePodCommentStore();
          podCommentStore.refreshComments(pod.id)
          .then(self.showSidemenuComments);

          const podLabelStore = usePodLabelStore();
          podLabelStore.refreshMlLabels(pod.id);
          podLabelStore.refreshLabels(pod.id);
        }

      })
    },
    transcodeFile: function() {
      this.preventTranscodingDoubleclick = true;
      this.errorMessage = "";
      this.errorTranslatable = null;

      let self = this;

      setTimeout(function () {
        self.preventTranscodingDoubleclick = false;
      }, 60000);

      let data = {
        id: this.podId,
      };

      const podStore = usePodStore();
      podStore.transcodeFile(data)
      .catch(function(error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const apiError = error.response.data.error.message;
          if (typeof apiError == 'object') {
            self.errorTranslatable = apiError;
          } else {
            self.errorMessage = apiError;
          }
        } else {
          self.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      });
    },
  },
  computed: {
    launchedServicesTooltipLabel() {
      if (!this.pod?.launchedServices?.length) {
        return '';
      }

      return this.pod.launchedServices
        .map((item) => this.$replaceT(item.code, item.params, item.content))
        .join(', ');
    },

    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    getComments: function() {
      const podCommentStore = usePodCommentStore();
      return podCommentStore.getComments(null);
    },
    getCommentsWithStart: function() {
      return this.getComments.filter(x => x.start !== null);
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isImagePod: function() {
      const podStore = usePodStore();
      return podStore.isImagePod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    isArchivePod: function() {
      const podStore = usePodStore();
      return podStore.isArchivePod;
    },
    isPdfPod: function() {
      return MimetypeHelper.isPdfAsset(this.getRawAsset);
    },
    isDocxPod: function() {
      return MimetypeHelper.isDocxAsset(this.getRawAsset)
    },
    canTranscodedFile: function() {
      let hasValidRawAsset = true;
      if (Array.isArray(this?.pod?.asset?.raw) && this.pod.asset.raw.length > 0) {
        const rawAsset = this.pod.asset.raw[0];

        if (rawAsset?.broken || rawAsset?.state !== 3) { // 3 = completed
          hasValidRawAsset = false;
        }
      }

      return hasValidRawAsset && !this.hasTranscodedFile;
    },
    hasTranscodedFile() {
      if (this.pod &&
          this.pod.asset &&
          this.pod.asset.web &&
          this.pod.asset.web.length > 0
      ) {
        return true;
      }

      return false;
    },
    hasThumbnailFile() {
      if (this.pod &&
          this.pod.asset &&
          this.pod.asset.thumbnail &&
          this.pod.asset.thumbnail.length > 0
      ) {
        return true;
      }

      return false;
    },
    getRawAsset() {
      if (this.pod &&
          this.pod.asset &&
          this.pod.asset.raw &&
          this.pod.asset.raw.length > 0)
      {
        return this.pod.asset.raw[0]
      }

      return null;
    },
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    isViewPodDeleted() {
      const podStore = usePodStore();
      return podStore.isViewPodDeleted;
    },
    isRefreshingPodArchiveContents() {
      const podStore = usePodStore();
      return podStore.getViewPodIsRefreshingPodArchiveContents;
    },
    listArchiveContentsError() {
      const podStore = usePodStore();
      return podStore.getViewPodListArchiveContentsError;
    },
    archiveContents() {
      const podStore = usePodStore();
      return podStore.getViewPodArchiveContents;
    },
  }
}
</script>

<style>
.launched-services-container {
  position: absolute;
  width: calc(100% - 34px);
  display: flex;
  justify-content: flex-end;

}

.launched-services-dot {
  width: 20px;
  height: 20px;
  background-color: #bb0000;
  border-radius: 10px;
  margin-top: 40px;
  margin-right: 40px;
  z-index: 1;

  animation-name: launched-services-dot-animation;
	animation-duration: 1s;
	animation-iteration-count: infinite;
	animation-direction: alternate;
}

.pod-loading-spinner {
  padding-top: 12px;
}

@keyframes launched-services-dot-animation {
	from { background-color: #FFFFFF; }
	to { background-color: #bb0000; }
}
</style>
